import { useReactionTypes, useReactions, useToggleReaction } from '../../hook/use-community'
import { CommunityEntityType, ReactionPayload, ReactionTypeDefinition } from '../../types/community'
import { toasterUtil } from '../../util/toaster-util'
import { Skeleton } from '../ui/skeleton'
import React from 'react'

interface ReactionButtonsProps {
  entityType: CommunityEntityType
  entityId: string
}

export const ReactionButtons = ({ entityId, entityType }: ReactionButtonsProps): React.ReactElement => {
  const { data: reactionTypes, isLoading: isLoadingTypes } = useReactionTypes()
  const { data: reactionSummary, isLoading: isLoadingSummary } = useReactions({ entityId, entityType })
  const mutation = useToggleReaction()

  const handleToggle = async (reactionTypeId: string): Promise<void> => {
    const payload: ReactionPayload = { entityId, entityType, reactionTypeId }
    await mutation.mutateAsync({ payload }).catch((err: Error) => toasterUtil.error(err.message))
  }

  if (isLoadingTypes || !reactionTypes) {
    return <div className="text-white/50">Loading reactions...</div>
  }

  if (isLoadingSummary) {
    return (
      <div className="inline-flex items-center gap-3 rounded-full bg-secondary-gray px-4 py-2">
        {reactionTypes.map((reactionType) => (
          <Skeleton key={reactionType.id} className="h-6 w-16" />
        ))}
      </div>
    )
  }

  const counts = reactionSummary?.counts ?? {}
  const activeReactionId = reactionSummary?.hustlerReactionType?.id

  return (
    <div className="inline-flex items-center gap-3 rounded-full bg-secondary-gray px-4 py-2">
      {reactionTypes.map((reactionType: ReactionTypeDefinition) => {
        const count = counts[reactionType.type] ?? 0
        const isActive = activeReactionId === reactionType.id

        return (
          <button
            key={reactionType.id}
            type="button"
            onClick={() => handleToggle(reactionType.id)}
            disabled={mutation.isLoading}
            className={`flex items-center gap-1.5 transition-colors ${
              isActive ? 'text-primary' : 'text-white hover:text-white/80'
            }`}
          >
            <span className="text-base">{reactionType.emoji}</span>
            <span className="text-sm font-normal" style={{ letterSpacing: '-0.02em' }}>
              {reactionType.label}
            </span>
            {count > 0 && (
              <span className="text-sm font-medium" style={{ letterSpacing: '-0.02em' }}>
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
